/**
 * Erinnerungen, Eskalationen und Übergaben (Entwicklerbericht „Erinnerungen und Übergaben“).
 *
 * Eine Regel: Wann (Auslöser + Stufen mit Zeitpunkt) → unter welchen Bedingungen → an wen → über
 * welche Kanäle. Die Planung läuft zentral auf dem Server; diese Datei enthält die reinen, testbaren
 * Zeitberechnungen und die lesbaren Sätze für Vorschau und Liste.
 *
 * Zeitsemantik (Z02): „24 Stunden vorher“ ist eine Dauer, „1 Kalendertag vorher um 09:00“ eine
 * lokale Kalenderregel in der Zeitzone der Regel – über Sommerzeitwechsel verschieden.
 */
import { addWorkdays, HOLIDAY_REGION_NAME, type HolidayRegion } from './holidays.js';
import { localClock } from './notify.js';

export const REMINDER_EVENTS = ['completed', 'reopened', 'blocked', 'unblocked', 'assigned'] as const;
export type ReminderEvent = (typeof REMINDER_EVENTS)[number];

export type ReminderTrigger =
  | { type: 'due' }
  | { type: 'start' }
  /** fester Termin: lokales Datum und Uhrzeit in der Zeitzone der Regel */
  | { type: 'fixed'; date: string; time: string }
  /** Eintritt in eine Spalte */
  | { type: 'column'; columnId: string }
  /** Aufgabenereignis */
  | { type: 'event'; event: ReminderEvent }
  /** Aufgabe wird startbereit (alle erforderlichen Vorgänger erledigt) */
  | { type: 'ready' };

export type ReminderOffset =
  | { mode: 'at' }
  /** Dauer in Minuten; negativ = davor */
  | { mode: 'duration'; minutes: number }
  /** Kalendertage (negativ = davor) zu einer lokalen Uhrzeit */
  | { mode: 'calendar'; days: number; time: string }
  /** 0.73: Arbeitstage (negativ = davor) zu einer lokalen Uhrzeit; Wochenenden und ggf. Feiertage zählen nicht */
  | { mode: 'workdays'; days: number; time: string; holidays?: HolidayRegion | null };

/** Datum + Arbeitstage nach der Regel einer Stufe */
export const workdayDate = (date: string, o: { days: number; holidays?: HolidayRegion | null }) =>
  addWorkdays(date, o.days, { weekends: true, holidays: o.holidays ?? null });

export interface ReminderRecipients {
  assignees?: boolean;
  owner?: boolean;
  watchers?: boolean;
  /** bei persönlichen Regeln: die anlegende Person */
  me?: boolean;
  users: string[];
  /** Teamgruppen: aufgelöst zu den jeweils aktuellen Mitgliedern mit Boardzugriff */
  groups: string[];
}

export type ReminderRepeat = ({ kind: 'interval'; minutes: number } | { kind: 'weekdays'; days: number[]; time: string }) & {
  /** Höchstzahl weiterer Meldungen (jede Wiederholung braucht ein Ende, Z04) */
  maxCount: number;
  /** spätestens bis zu diesem lokalen Datum */
  until?: string | null;
};

export interface ReminderStage {
  offset: ReminderOffset;
  recipients: ReminderRecipients;
  text?: string | null;
  repeat?: ReminderRepeat | null;
}

export type ReminderScope = 'board' | 'card' | 'personal';

export interface ReminderChannels {
  email: boolean;
  push: boolean;
}

export interface ReminderRule {
  id: string;
  boardId: string;
  cardId: string | null;
  scope: ReminderScope;
  /** persönliche Regel: die einzige Empfängerin bzw. der einzige Empfänger */
  ownerId: string | null;
  /** Kartenausnahme zu einem Boardstandard (der Standard läuft für diese Karte dann nicht) */
  overridesRuleId: string | null;
  name: string | null;
  enabled: boolean;
  trigger: ReminderTrigger;
  stages: ReminderStage[];
  channels: ReminderChannels;
  /** nur solange die Aufgabe offen ist (Standard für Frist/Start) */
  onlyOpen: boolean;
  /** bei Ereignisregeln die auslösende Person mitbenachrichtigen */
  includeActor: boolean;
  timeZone: string;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** vom Server ergänzt */
  next?: string | null;
  /** vom Server ergänzt: Zustand für die Vorschau, z. B. „Wartet auf Fälligkeitsdatum“ */
  state?: string | null;
}

/** Eine geplante oder gelaufene Ausführung (Historie unter der Regel, K03) */
export interface ReminderRun {
  id: number;
  stage: number;
  occurrence: number;
  dueAt: string;
  status: 'pending' | 'done' | 'skipped' | 'failed' | 'cancelled';
  result: string | null;
  finishedAt: string | null;
}

/** Vorschau (U03, P12): nächste konkrete Zeitpunkte und aktuell passende Empfänger – ohne Versand */
export interface ReminderPreview {
  state: string | null;
  stages: { at: string | null; past: boolean; recipients: { id: string; name: string }[]; problems: string[] }[];
}

export const REMINDER_STATUS_LABEL: Record<ReminderRun['status'], string> = {
  pending: 'geplant',
  done: 'gesendet',
  skipped: 'übersprungen',
  failed: 'fehlgeschlagen',
  cancelled: 'entfallen',
};

/* ---------- Zeitberechnung ---------- */

/** Abstand der Ortszeit zu UTC in Minuten für diesen Zeitpunkt */
export function tzOffsetMinutes(d: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(d);
  const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second'));
  return Math.round((asUtc - Math.floor(d.getTime() / 1000) * 1000) / 60_000);
}

/**
 * Lokales Datum + Uhrzeit in einer Zeitzone → Zeitpunkt. Eine Uhrzeit in der Lücke der
 * Sommerzeitumstellung rückt nach vorn (02:30 → 03:30).
 */
export function zonedTime(date: string, time: string, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const guess = Date.UTC(y!, m! - 1, d!, hh ?? 0, mm ?? 0);
  let t = guess - tzOffsetMinutes(new Date(guess), timeZone) * 60_000;
  t = guess - tzOffsetMinutes(new Date(t), timeZone) * 60_000;
  return new Date(t);
}

export function addLocalDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

export const localDateOf = (d: Date, timeZone: string) => localClock(d, timeZone).date;

/** Bezugspunkt einer Frist bzw. eines Starts: Zeitpunkt und lokales Datum */
export interface TriggerBase {
  instant: Date;
  date: string;
}

/**
 * Frist: mit Uhrzeit der Zeitpunkt; ganztägig das Tagesende (wie bisher) in der Zone der Regel.
 * Start: mit Uhrzeit der Zeitpunkt; ganztägig 09:00 in der Zone (nie unbemerkt Mitternacht, Z02).
 */
export function triggerBase(kind: 'due' | 'start', iso: string, hasTime: boolean, timeZone: string): TriggerBase {
  if (hasTime) return { instant: new Date(iso), date: localDateOf(new Date(iso), timeZone) };
  const date = iso.slice(0, 10);
  return kind === 'due' ? { instant: zonedTime(addLocalDays(date, 1), '00:00', timeZone), date } : { instant: zonedTime(date, '09:00', timeZone), date };
}

export function stageTime(offset: ReminderOffset, base: TriggerBase, timeZone: string): Date {
  switch (offset.mode) {
    case 'at':
      return base.instant;
    case 'duration':
      return new Date(base.instant.getTime() + offset.minutes * 60_000);
    case 'calendar':
      return zonedTime(addLocalDays(base.date, offset.days), offset.time, timeZone);
    case 'workdays':
      return zonedTime(workdayDate(base.date, offset), offset.time, timeZone);
  }
}

/** Nächste Wiederholung nach `prev` (oder null, wenn das Ende erreicht ist) */
export function nextRepeat(repeat: ReminderRepeat, prev: Date, occurrence: number, timeZone: string): Date | null {
  // occurrence = Nummer der kommenden Wiederholung (1 = erste); maxCount = Zahl der Wiederholungen
  if (occurrence > repeat.maxCount) return null;
  let next: Date | null = null;
  if (repeat.kind === 'interval') next = new Date(prev.getTime() + Math.max(5, repeat.minutes) * 60_000);
  else {
    const start = localDateOf(prev, timeZone);
    for (let i = 0; i < 14 && !next; i++) {
      const date = addLocalDays(start, i);
      const [y, m, d] = date.split('-').map(Number);
      const wd = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
      if (!repeat.days.includes(wd)) continue;
      const at = zonedTime(date, repeat.time, timeZone);
      if (at > prev) next = at;
    }
  }
  if (!next) return null;
  if (repeat.until && localDateOf(next, timeZone) > repeat.until) return null;
  return next;
}

/* ---------- Lesbare Sätze ---------- */

export function durationText(minutes: number): string {
  const m = Math.abs(minutes);
  if (m % 1440 === 0 && m >= 2880) return `${m / 1440} Tage`;
  if (m % 60 === 0) return m === 60 ? '1 Stunde' : `${m / 60} Stunden`;
  return m === 1 ? '1 Minute' : `${m} Minuten`;
}

const ANCHOR: Record<ReminderTrigger['type'], string> = {
  due: 'Fälligkeit',
  start: 'Start',
  fixed: 'Termin',
  column: 'Spaltenwechsel',
  event: 'Ereignis',
  ready: 'Startbereitschaft',
};

export const EVENT_LABEL: Record<ReminderEvent, string> = {
  completed: 'Aufgabe erledigt',
  reopened: 'Aufgabe wieder geöffnet',
  blocked: 'Aufgabe blockiert',
  unblocked: 'Blockierung aufgehoben',
  assigned: 'Zuständigkeit geändert',
};

/** „24 Stunden vor Fälligkeit“, „1 Kalendertag danach, 09:00 Uhr“ … */
export function offsetText(trigger: ReminderTrigger, o: ReminderOffset, columnName?: string): string {
  if (trigger.type === 'fixed') return `Am ${formatDateDe(trigger.date)} um ${trigger.time} Uhr`;
  const anchor =
    trigger.type === 'column'
      ? `Wechsel nach „${columnName ?? 'Spalte'}“`
      : trigger.type === 'event'
        ? `„${EVENT_LABEL[trigger.event]}“`
        : trigger.type === 'ready'
          ? 'Startbereitschaft'
          : ANCHOR[trigger.type];
  const event = trigger.type === 'column' || trigger.type === 'event' || trigger.type === 'ready';
  if (o.mode === 'at') {
    if (trigger.type === 'due') return 'Bei Fälligkeit';
    if (trigger.type === 'start') return 'Zum Start';
    return `Sofort bei ${anchor}`;
  }
  if (o.mode === 'duration') {
    if (o.minutes === 0) return event ? `Sofort bei ${anchor}` : `Bei ${anchor}`;
    return `${durationText(o.minutes)} ${o.minutes < 0 ? 'vor' : 'nach'} ${event ? anchor : anchor === 'Fälligkeit' ? 'Fälligkeit' : anchor}`;
  }
  const d = Math.abs(o.days);
  if (o.mode === 'workdays') {
    const where = o.holidays ? ` (ohne Wochenenden und Feiertage in ${HOLIDAY_REGION_NAME[o.holidays]})` : ' (ohne Wochenenden)';
    const day0 = o.days === 0 ? `Am Tag ${trigger.type === 'due' ? 'der Fälligkeit' : 'des Starts'} bzw. nächsten Arbeitstag` : `${d === 1 ? '1 Arbeitstag' : `${d} Arbeitstage`} ${o.days < 0 ? 'davor' : 'danach'}`;
    return `${day0}, ${o.time} Uhr${o.days === 0 ? '' : where}`;
  }
  const day = o.days === 0 ? `Am Tag ${trigger.type === 'due' ? 'der Fälligkeit' : 'des Starts'}` : `${d === 1 ? '1 Kalendertag' : `${d} Kalendertage`} ${o.days < 0 ? 'davor' : 'danach'}`;
  return `${day}, ${o.time} Uhr`;
}

export function formatDateDe(date: string): string {
  const [y, m, d] = date.split('-');
  return `${Number(d)}.${Number(m)}.${y}`;
}

/** „An alle Zuständigen, Hauptverantwortliche und Gestaltung“ */
export function recipientsText(r: ReminderRecipients, names: { users?: Record<string, string>; groups?: Record<string, string> } = {}, personal = false): string {
  if (personal) return 'Nur an mich';
  const parts: string[] = [];
  if (r.assignees) parts.push('alle Zuständigen');
  if (r.owner) parts.push('Hauptverantwortliche');
  if (r.watchers) parts.push('Beobachtende');
  if (r.me) parts.push('mich');
  for (const g of r.groups) parts.push(names.groups?.[g] ? `Gruppe „${names.groups[g]}“` : 'eine gelöschte Gruppe');
  for (const u of r.users) parts.push(names.users?.[u] ?? 'eine Person ohne Zugriff');
  if (!parts.length) return 'Keine Empfänger gewählt';
  return `An ${parts.length > 1 ? `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}` : parts[0]}`;
}

export const SCOPE_LABEL: Record<ReminderScope, string> = {
  board: 'Boardstandard',
  card: 'Eigene Kartenregel',
  personal: 'Nur für mich',
};

/** Vorlagen (U02) – Ausgangspunkt für eine neue Regel */
export const REMINDER_TEMPLATES: { key: string; label: string; trigger: ReminderTrigger; stages: ReminderStage[] }[] = [
  {
    key: 'before-due',
    label: 'Vor Frist erinnern',
    trigger: { type: 'due' },
    stages: [{ offset: { mode: 'duration', minutes: -1440 }, recipients: { assignees: true, users: [], groups: [] } }],
  },
  {
    key: 'overdue',
    label: 'Bei Überfälligkeit eskalieren',
    trigger: { type: 'due' },
    stages: [
      { offset: { mode: 'at' }, recipients: { assignees: true, owner: true, users: [], groups: [] } },
      { offset: { mode: 'calendar', days: 1, time: '09:00' }, recipients: { assignees: true, owner: true, users: [], groups: [] } },
    ],
  },
  {
    key: 'column',
    label: 'Bei Spaltenwechsel',
    trigger: { type: 'column', columnId: '' },
    stages: [{ offset: { mode: 'at' }, recipients: { assignees: true, users: [], groups: [] } }],
  },
  {
    key: 'ready',
    label: 'Wenn Aufgabe startbereit',
    trigger: { type: 'ready' },
    stages: [{ offset: { mode: 'at' }, recipients: { assignees: true, users: [], groups: [] } }],
  },
];
